<template>
  <div v-if="visible" class="strategy-modal">
    <section class="strategy-modal__panel" role="dialog" aria-modal="true" aria-labelledby="strategy-modal-title">
      <header class="strategy-modal__header">
        <h2 id="strategy-modal-title">启动新机器人</h2>
        <button type="button" :disabled="submitting" aria-label="关闭" @click="close">
          <i class="el-icon-close" />
        </button>
      </header>

      <!-- 机器人名称暂时隐藏，后续需要时取消此处注释。
      <div class="strategy-field">
        <label for="strategy-robot-name">机器人名称</label>
        <input
          id="strategy-robot-name"
          v-model.trim="form.robotName"
          maxlength="40"
          placeholder="例如：合约AI · BTC 趋势 2 号"
        />
      </div>
      -->

      <div class="strategy-field">
        <label>交易所</label>
        <div class="strategy-exchange-readonly">
          <i />
          <strong>{{ currentExchangeOption ? currentExchangeOption.name : (currentExchange || '--') }}</strong>
        </div>
      </div>

      <div class="strategy-field">
        <label>策略类型</label>
        <div class="strategy-product-readonly">
          <i :style="{ background: product.gradient }" />
          <strong>{{ product.name }}</strong>
        </div>
      </div>

      <div v-if="setupLoading" class="strategy-interface-state loading">
        <i class="el-icon-loading" />
        正在读取机器人状态、可用名额、交易所余额与策略参数…
      </div>
      <div v-else-if="blockingIssue" class="strategy-interface-state error">
        <i class="el-icon-warning-outline" />
        {{ blockingIssue }}
      </div>

      <template v-if="['bb', 'grid', 'martin'].includes(product.kind)">
        <div class="strategy-field">
          <label for="strategy-base-coin">仓位币种</label>
          <el-select
            id="strategy-base-coin"
            v-model="form.baseCoin"
            class="strategy-filter-select"
            filterable
            clearable
            :disabled="setupLoading"
            placeholder="输入币种名称筛选"
            @change="handleBaseCoinChange"
          >
            <el-option
              v-for="item in setup.coins"
              :key="item.coin"
              :label="item.coin"
              :value="item.coin"
            />
          </el-select>
        </div>

        <div class="strategy-field">
          <label for="strategy-pair">交易对</label>
          <el-select
            id="strategy-pair"
            v-model="pairIndex"
            class="strategy-filter-select"
            filterable
            clearable
            :disabled="!form.baseCoin || pairLoading"
            :placeholder="pairLoading ? '正在读取交易对…' : '输入币种或交易对筛选'"
            @change="handlePairChange"
          >
            <el-option
              v-for="(item, index) in pairs"
              :key="`${item.target_coin}-${item.cost_coin}-${index}`"
              :label="`${item.target_coin} / ${item.cost_coin}`"
              :value="index"
            />
          </el-select>
        </div>
      </template>

      <template v-if="product.kind === 'bb' && selectedPair">
        <div class="strategy-two-column">
          <div class="strategy-field">
            <label>{{ form.baseCoin }} 仓位</label>
            <div class="strategy-input-unit">
              <input v-model.trim="form.baseAmount" inputmode="decimal" placeholder="可只填写一侧" />
              <span>{{ form.baseCoin }}</span>
            </div>
            <small>可用 {{ formatValue(baseBalance) }}；最小 {{ formatValue(baseMinimum) }}</small>
          </div>
          <div class="strategy-field">
            <label>{{ form.quoteCoin }} 仓位</label>
            <div class="strategy-input-unit">
              <input v-model.trim="form.quoteAmount" inputmode="decimal" placeholder="可只填写一侧" />
              <span>{{ form.quoteCoin }}</span>
            </div>
            <small>可用 {{ formatValue(quoteBalance) }}；最小 {{ formatValue(quoteMinimum) }}</small>
          </div>
        </div>
      </template>

      <template v-else-if="product.kind === 'grid' && selectedPair">
        <div class="strategy-two-column">
          <div class="strategy-field">
            <label>区间最低价</label>
            <input v-model.trim="form.minPrice" inputmode="decimal" placeholder="请输入最低价" />
          </div>
          <div class="strategy-field">
            <label>区间最高价</label>
            <input v-model.trim="form.maxPrice" inputmode="decimal" placeholder="请输入最高价" />
          </div>
        </div>
        <div class="strategy-two-column">
          <div class="strategy-field">
            <label>网格数量</label>
            <input v-model.trim="form.gridNumber" inputmode="numeric" placeholder="2 - 150" />
          </div>
          <div class="strategy-field">
            <label>网格模式</label>
            <select v-model.number="form.gridMode">
              <option :value="0">等差网格</option>
              <option :value="1">等比网格</option>
            </select>
          </div>
        </div>
        <div class="strategy-two-column">
          <div class="strategy-field">
            <label>{{ form.quoteCoin }} 投入</label>
            <div class="strategy-input-unit">
              <input v-model.trim="form.quoteAmount" inputmode="decimal" placeholder="可只填写一侧" />
              <span>{{ form.quoteCoin }}</span>
            </div>
            <small>可用 {{ formatValue(quoteBalance) }}；最小 {{ formatValue(gridQuoteMinimum) }}</small>
          </div>
          <div class="strategy-field">
            <label>{{ form.baseCoin }} 投入</label>
            <div class="strategy-input-unit">
              <input v-model.trim="form.baseAmount" inputmode="decimal" placeholder="可只填写一侧" />
              <span>{{ form.baseCoin }}</span>
            </div>
            <small>可用 {{ formatValue(baseBalance) }}；最小 {{ formatValue(gridBaseMinimum) }}</small>
          </div>
        </div>
      </template>

      <template v-else-if="product.kind === 'martin' && selectedPair">
        <div class="strategy-two-column">
          <div class="strategy-field">
            <label>策略类型</label>
            <select v-model.number="form.strategyType">
              <option :value="0">仓位马丁策略</option>
              <option :value="1">盈利预期马丁策略</option>
              <option :value="2">双马丁策略</option>
            </select>
          </div>
          <div class="strategy-field">
            <label>趋势周期</label>
            <select v-model.number="form.trendCycle">
              <option :value="1">1 分钟</option>
              <option :value="5">5 分钟</option>
              <option :value="15">15 分钟</option>
              <option :value="30">30 分钟</option>
            </select>
          </div>
        </div>
        <div class="strategy-two-column">
          <div class="strategy-field">
            <label>首单额度 (USDT)</label>
            <input v-model.trim="form.firstSpace" inputmode="decimal" placeholder="请输入首单额度" />
          </div>
          <div class="strategy-field">
            <label>循环次数</label>
            <input v-model.trim="form.cycleNumber" inputmode="numeric" placeholder="0 表示无限" />
          </div>
        </div>
        <div class="strategy-two-column">
          <div class="strategy-field">
            <label>首单倍数</label>
            <input v-model.trim="form.firstMultiple" inputmode="decimal" placeholder="请输入首单倍数" />
          </div>
          <div class="strategy-field">
            <label>做单数量</label>
            <input v-model.trim="form.billNumber" inputmode="numeric" placeholder="请输入做单数量" />
          </div>
        </div>
        <div class="strategy-two-column">
          <div class="strategy-field">
            <label>止盈比例 (%)</label>
            <input v-model.trim="form.stopRatio" inputmode="decimal" placeholder="请输入止盈比例" />
          </div>
          <div class="strategy-field">
            <label>止盈回调 (%)</label>
            <input v-model.trim="form.stopCallback" inputmode="decimal" placeholder="请输入止盈回调" />
          </div>
        </div>
        <div class="strategy-field">
          <label>满单止损比例 (%)</label>
          <input v-model.trim="form.fullStopRatio" inputmode="decimal" placeholder="请输入止损比例" />
          <small>做单数量变化时，补仓配置将同步更新。</small>
        </div>
      </template>

      <template v-else-if="!['bb', 'grid', 'martin'].includes(product.kind)">
        <div v-if="product.kind !== 'astra'" class="strategy-field">
          <label for="strategy-coin">交易币种</label>
          <el-select
            id="strategy-coin"
            v-model="form.coin"
            class="strategy-filter-select"
            filterable
            clearable
            :disabled="setupLoading"
            placeholder="输入币种名称筛选"
            @change="handleCoinChange"
          >
            <el-option
              v-for="item in setup.coins"
              :key="item.coin"
              :label="coinOptionLabel(item)"
              :value="item.coin"
            />
          </el-select>
        </div>

        <div class="strategy-field">
          <label for="strategy-amount">投入金额 (USDT)</label>
          <div class="strategy-input-unit">
            <input
              id="strategy-amount"
              v-model.trim="form.amount"
              inputmode="decimal"
              placeholder="请输入投入金额"
            />
            <span>USDT</span>
          </div>
          <div v-if="quickAmounts.length" class="strategy-quick">
            <button
              v-for="item in quickAmounts"
              :key="item.label"
              type="button"
              @click="form.amount = String(item.value)"
            >
              {{ item.label }}
            </button>
          </div>
          <div class="strategy-balance">
            <span>支付账户 · 交易所账户</span>
            <strong>可用 {{ formatValue(setup.balance) }} USDT</strong>
          </div>
          <small v-if="coinDetail.minSpace !== null">
            当前币种最小仓位：{{ formatValue(coinDetail.minSpace) }} USDT
          </small>
          <small v-else-if="product.kind === 'astra'">Astra 最小投入 100 USDT</small>
        </div>
      </template>

      <div
        v-if="(product.kind === 'vip' && form.versionType === 2) || (product.kind === 'hedge' && product.singleDirection && Number(robotType) === 1)"
        class="strategy-field"
      >
        <label>杠杆倍数</label>
        <input
          v-model.number="form.lever"
          type="number"
          min="1"
          :max="coinDetail.maxLever || 125"
          @change="reloadCoinDetail"
        />
        <small v-if="coinDetail.maxLever !== null">最大杠杆：{{ coinDetail.maxLever }}x</small>
      </div>

      <template v-if="product.kind === 'hedge'">
        <div v-if="product.singleDirection" class="strategy-two-column">
          <div class="strategy-field">
            <label>合约类型</label>
            <select v-model.number="form.futureType" @change="handleFutureTypeChange">
              <option :value="1">U 本位</option>
              <option v-if="Number(robotType) < 2" :value="2">币本位</option>
            </select>
          </div>
          <div class="strategy-field">
            <label>交易方向</label>
            <select v-model.number="form.direction">
              <option :value="1">做多</option>
              <option :value="2">做空</option>
            </select>
          </div>
        </div>

        <div class="strategy-field">
          <label>止损设置</label>
          <div class="strategy-options">
            <button :class="{ active: form.stopLoss === 100 }" type="button" @click="form.stopLoss = 100">
              止损
            </button>
            <button
              v-if="!product.singleDirection"
              :class="{ active: form.stopLoss === 5 }"
              type="button"
              @click="form.stopLoss = 5"
            >
              部分止损
            </button>
            <button :class="{ active: form.stopLoss === 0 }" type="button" @click="form.stopLoss = 0">
              不止损
            </button>
          </div>
        </div>

        <div v-if="form.stopLoss !== 0" class="strategy-field strategy-loss-ratio">
          <div class="strategy-loss-ratio__header">
            <label>{{ $t('补亏损比例') }}</label>
            <strong>{{ lossTransferRateValue }}%</strong>
          </div>
          <input
            v-model.number="form.lossTransferRate"
            class="strategy-loss-ratio__slider"
            type="range"
            min="0"
            max="100"
            step="1"
            :aria-label="$t('补亏损比例')"
            :style="{ '--loss-progress': lossTransferRateProgress }"
          />
        </div>
      </template>

      <div v-if="isStandardHedge" class="strategy-field strategy-risk-level">
        <div class="strategy-risk-level__header">
          <label
            title="风险等级越高，收益和风险越高，风险等级越低，收益越低越稳定；"
          >风险等级</label>
          <span :class="'is-' + riskLevelTone">{{ riskLevelName }}</span>
        </div>
        <div class="strategy-risk-level__steps">
          <div class="strategy-risk-level__track" aria-hidden="true"></div>
          <div
            class="strategy-risk-level__track-active"
            :style="{ '--risk-progress': riskLevelTrackWidth }"
            aria-hidden="true"
          ></div>
          <button
            v-for="item in riskLevelOptions"
            :key="item"
            type="button"
            :class="{
              active: Number(form.positionProportion) === item,
              disabled: isRiskLevelDisabled(item)
            }"
            :disabled="isRiskLevelDisabled(item)"
            @click="selectRiskLevel(item)"
          >
            <i aria-hidden="true"></i>
            <span>{{ item }}%</span>
          </button>
        </div>
      </div>

      <div v-if="['hedge', 'term'].includes(product.kind)" class="strategy-field">
        <div class="strategy-field__label-row">
          <label>最大持仓设置</label>
          <label class="strategy-checkbox">
            <input v-model="form.infinite" type="checkbox" />
            无限
          </label>
        </div>
        <div v-if="!form.infinite" class="strategy-input-unit">
          <input v-model.trim="form.maxPosition" inputmode="decimal" placeholder="不得小于本次投入金额" />
          <span>USDT</span>
        </div>
        <div v-else class="strategy-infinite">无限大</div>
      </div>

      <div v-if="setupErrors.length" class="strategy-interface-errors">
        <span v-for="error in setupErrors" :key="error">{{ error }}</span>
      </div>

      <div class="strategy-risk">
        ● 风险保护已启用：自动止损、保证金监控与异常告警将同步生效。
      </div>

      <footer class="strategy-modal__footer">
        <button type="button" :disabled="submitting" @click="close">取消</button>
        <button
          class="primary"
          type="button"
          :disabled="submitting || setupLoading || Boolean(blockingIssue)"
          @click="submit"
        >
          {{ submitting ? '启动中…' : '确认启动' }}
        </button>
      </footer>
    </section>
  </div>
</template>

<script>
import {
  getSpotPairMinimums,
  getStrategyProduct,
  loadSpotPairs,
  loadStrategyBalance,
  loadStrategyCoinDetail,
  loadStrategySetup,
  normalizeExchangeRobots,
  strategyAvailabilityIssue,
  strategySpaceAvailabilityIssue
} from '@/api/strategy'
import { languageIntlLocale } from '@/utils/locale'

function emptySetup() {
  return {
    robotInfo: null,
    capacity: null,
    balance: null,
    spotBalance: null,
    minimum: null,
    coins: [],
    errors: []
  }
}

function emptyCoinDetail() {
  return { minSpace: null, minSpaceTrue: null, maxLever: null }
}

function defaultForm() {
  return {
    robotName: '',
    coin: '',
    amount: '',
    lever: 10,
    stopLoss: 100,
    versionType: 1,
    lossTransferRate: '70',
    positionProportion: 70,
    direction: 1,
    futureType: 1,
    infinite: true,
    maxPosition: '',
    baseCoin: '',
    quoteCoin: '',
    baseAmount: '',
    quoteAmount: '',
    minPrice: '',
    maxPrice: '',
    gridNumber: '10',
    gridMode: 0,
    strategyType: 0,
    trendCycle: 1,
    firstSpace: '15',
    cycleNumber: '0',
    firstMultiple: '5',
    billNumber: '10',
    stopRatio: '1.5',
    stopCallback: '0.2',
    trackStop: 0,
    trackReplenishment: 1,
    fullStop: 1,
    fullStopRatio: '5',
    trendCycleSwitch: 0
  }
}

function finiteNumber(value) {
  if (value === '' || value === null || value === undefined) return null
  const result = Number(value)
  return Number.isFinite(result) ? result : null
}

const RISK_LEVEL_OPTIONS = [50, 60, 70, 80, 100]

function buildMartinConfig(strategyType, billNumber) {
  const count = Math.max(1, Math.min(31, Math.floor(Number(billNumber) || 1) - 1))
  const policyType = Number(strategyType) + 1
  const replenishmentConf = []
  const replenishmentCallback = []
  for (let index = 1; index <= count; index += 1) {
    if (policyType === 1) {
      const previous = replenishmentConf[index - 2]
      let decline = 1
      if (index > 1 && index <= 3) decline = 1 + Number(previous.decline)
      if (index >= 4 && index <= 5) decline = 1.5 + Number(previous.decline)
      if (index === 6 || index >= 8) decline = 3.5 + Number(previous.decline)
      if (index === 7) decline = 4.5 + Number(previous.decline)
      replenishmentConf.push({
        decline,
        multiple: index === 1 ? 2 : Number(previous.multiple) * 2,
        id: index
      })
      replenishmentCallback.push({ backValue: 0.15, id: index })
    } else {
      replenishmentConf.push({ decline: policyType, multiple: 1, id: index })
      replenishmentCallback.push({ backValue: 1, id: index })
    }
  }
  return { replenishmentConf, replenishmentCallback }
}

export default {
  name: 'StrategyCreateModal',
  props: {
    visible: { type: Boolean, default: false },
    initialProductKey: { type: String, default: 'astra' },
    initialCapacity: { type: Object, default: null },
    exchangeRobots: { type: Array, default: () => [] },
    currentExchange: { type: String, default: '' },
    robotInfo: { type: Object, default: null },
    submitting: { type: Boolean, default: false }
  },
  data() {
    return {
      productKey: 'astra',
      robotType: null,
      setupLoading: false,
      setup: emptySetup(),
      coinDetail: emptyCoinDetail(),
      form: defaultForm(),
      pairs: [],
      pairIndex: null,
      pairLoading: false,
      baseBalance: null,
      quoteBalance: null,
      requestSequence: 0
    }
  },
  computed: {
    product() {
      return getStrategyProduct(this.productKey)
    },
    exchangeOptions() {
      return normalizeExchangeRobots(this.exchangeRobots)
    },
    currentExchangeOption() {
      const current = String(this.currentExchange || '').toLowerCase()
      return this.exchangeOptions.find(item =>
        current && item.name.toLowerCase() === current
      )
    },
    setupErrors() {
      return Array.from(new Set(this.setup.errors || []))
    },
    blockingIssue() {
      if (!this.currentExchangeOption) return '当前交易所暂不可用'
      if (this.robotType === null || this.robotType === undefined) return '请选择交易所'
      if (!this.product.exchanges.includes(Number(this.robotType))) {
        return `${this.currentExchangeOption.name} 暂不支持创建该策略`
      }
      if (this.setupLoading) return ''
      if (!this.setup.robotInfo) return '机器人状态暂不可用，不能创建策略'
      if (Number(this.setup.robotInfo.bind_api) !== 1) return '当前交易所尚未绑定有效 API'
      const availability = strategyAvailabilityIssue(this.productKey, this.setup.capacity)
      if (availability) return availability
      if (this.product.kind !== 'astra' && !this.setup.coins.length) {
        return '当前没有可创建策略的币种'
      }
      return ''
    },
    quickAmounts() {
      const balance = finiteNumber(this.setup.balance)
      if (balance === null || balance <= 0) return []
      return [
        { label: '25%', value: Number((balance * 0.25).toFixed(8)) },
        { label: '50%', value: Number((balance * 0.5).toFixed(8)) },
        { label: '100%', value: Number(balance.toFixed(8)) }
      ]
    },
    lossTransferRateValue() {
      const value = finiteNumber(this.form.lossTransferRate)
      if (value === null) return 0
      return Math.min(100, Math.max(0, Math.round(value)))
    },
    lossTransferRateProgress() {
      return `${this.lossTransferRateValue}%`
    },
    isStandardHedge() {
      return this.product.kind === 'hedge' && this.product.speed === 0 && !this.product.singleDirection
    },
    riskLevelOptions() {
      return RISK_LEVEL_OPTIONS
    },
    riskLevelName() {
      const value = Number(this.form.positionProportion)
      if (value < 60) return '低风险'
      if (value <= 80) return '中风险'
      return '高风险'
    },
    riskLevelTone() {
      const value = Number(this.form.positionProportion)
      if (value < 60) return 'low'
      if (value <= 80) return 'middle'
      return 'high'
    },
    riskLevelTrackWidth() {
      const index = RISK_LEVEL_OPTIONS.indexOf(Number(this.form.positionProportion))
      return [0, 25, 50, 75, 100][Math.max(0, index)] + '%'
    },
    selectedPair() {
      return Number.isInteger(this.pairIndex) ? this.pairs[this.pairIndex] : null
    },
    pairMinimums() {
      return getSpotPairMinimums(this.selectedPair, this.robotType)
    },
    baseMinimum() {
      if (!this.selectedPair) return null
      return this.form.baseCoin === this.selectedPair.target_coin
        ? this.pairMinimums.target
        : this.pairMinimums.cost
    },
    quoteMinimum() {
      if (!this.selectedPair) return null
      return this.form.quoteCoin === this.selectedPair.target_coin
        ? this.pairMinimums.target
        : this.pairMinimums.cost
    },
    gridMinimums() {
      const pair = this.selectedPair
      const gridNumber = finiteNumber(this.form.gridNumber)
      const maxPrice = finiteNumber(this.form.maxPrice)
      if (!pair || gridNumber === null) return { base: null, quote: null }
      if (Number(this.robotType) === 1) {
        const minimum = finiteNumber(pair.min_qty)
        return {
          base: minimum === null ? null : minimum * gridNumber,
          quote: minimum === null || maxPrice === null ? null : minimum * maxPrice * gridNumber
        }
      }
      const baseMinimum = finiteNumber(pair.min_qty_result)
      const quoteMinimum = finiteNumber(pair.min_notional)
      return {
        base: baseMinimum === null ? null : baseMinimum * gridNumber * 1.5,
        quote: quoteMinimum === null ? null : quoteMinimum * gridNumber * 1.5
      }
    },
    gridBaseMinimum() {
      return this.gridMinimums.base
    },
    gridQuoteMinimum() {
      return this.gridMinimums.quote
    },
    gridSingleProfit() {
      const minPrice = finiteNumber(this.form.minPrice)
      const maxPrice = finiteNumber(this.form.maxPrice)
      const gridNumber = finiteNumber(this.form.gridNumber)
      if (minPrice === null || maxPrice === null || gridNumber === null || gridNumber <= 0) return null
      const interval = (maxPrice - minPrice) / gridNumber
      if (interval <= 0 || maxPrice <= interval) return null
      return (interval - (maxPrice + maxPrice - interval) * 0.001) / (maxPrice - interval)
    }
  },
  watch: {
    visible(value) {
      if (value) this.initialize()
    },
    'form.amount'() {
      this.syncStandardRiskLevel()
    }
  },
  methods: {
    close() {
      if (!this.submitting) this.$emit('update:visible', false)
    },
    initialize() {
      this.productKey = getStrategyProduct(this.initialProductKey).key
      this.chooseCurrentExchange()
      this.resetProductForm()
      this.fetchSetup()
    },
    chooseCurrentExchange() {
      this.robotType = this.currentExchangeOption
        ? this.currentExchangeOption.robotType
        : null
    },
    resetProductForm() {
      this.form = defaultForm()
      this.form.versionType = this.product.versionType || 1
      if (this.product.kind === 'vip') this.form.stopLoss = 1
      if (this.product.speed === 1 || this.product.singleDirection) {
        this.form.stopLoss = 0
        this.form.positionProportion = 100
      }
      if (this.product.singleDirection) {
        if (Number(this.robotType) === 0) this.form.futureType = 2
        else if (Number(this.robotType) === 1) this.form.futureType = 1
        else this.form.futureType = ''
      }
      this.setup = emptySetup()
      this.coinDetail = emptyCoinDetail()
      this.pairs = []
      this.pairIndex = null
      this.baseBalance = null
      this.quoteBalance = null
    },
    async fetchSetup() {
      if (this.robotType === null || this.robotType === undefined) return
      if (!this.product.exchanges.includes(Number(this.robotType))) return
      const sequence = ++this.requestSequence
      this.setupLoading = true
      const result = await loadStrategySetup(this.productKey, this.robotType, {
        capacity: this.initialCapacity,
        robotInfo: this.robotInfo
      })
      if (sequence !== this.requestSequence) return
      this.setup = result
      if (this.product.kind === 'astra') {
        this.coinDetail = { ...emptyCoinDetail(), minSpace: result.minimum }
      }
      this.setupLoading = false
    },
    async handleCoinChange() {
      this.form.amount = ''
      this.coinDetail = emptyCoinDetail()
      if (this.isStandardHedge) this.form.positionProportion = 70
      const selected = this.setup.coins.find(item => item.coin === this.form.coin)
      if (this.product.kind === 'vip') {
        this.form.versionType = selected && selected.versionType
          ? selected.versionType
          : this.product.versionType
        // Keep the App viphedgeSelectCoin behavior: reset leverage on every coin change.
        // Multi-currency uses versionType=3, requests with an empty coin, and has fixed leverage.
        this.form.lever = 10
      }
      if (!this.form.coin) return
      await this.reloadCoinDetail()
      if (
        this.product.kind === 'hedge' &&
        this.product.singleDirection &&
        Number(this.robotType) === 0 &&
        Number(this.form.futureType) === 2
      ) {
        await this.reloadHedgeBalance()
      }
    },
    async handleFutureTypeChange() {
      await Promise.all([
        this.reloadCoinDetail(),
        this.reloadHedgeBalance()
      ])
    },
    async reloadHedgeBalance() {
      if (this.product.kind !== 'hedge') return
      const coin = this.product.singleDirection && Number(this.form.futureType) === 2
        ? this.form.coin
        : 'USDT'
      if (!coin) return
      const result = await loadStrategyBalance(this.robotType, coin, {
        type: 1,
        instType: '',
        futureType: this.form.futureType
      })
      this.setup = { ...this.setup, balance: result.balance }
      if (result.error) this.$message.error(result.error)
    },
    async reloadCoinDetail() {
      if (!this.form.coin || !['vip', 'term', 'hedge'].includes(this.product.kind)) return
      const sequence = ++this.requestSequence
      const result = await loadStrategyCoinDetail(
        this.productKey,
        this.robotType,
        this.form.coin,
        {
          lever: this.form.lever,
          futureType: this.form.futureType,
          positionProportion: this.form.positionProportion,
          versionType: this.form.versionType
        }
      )
      if (sequence !== this.requestSequence) return
      this.coinDetail = result.data || emptyCoinDetail()
      this.syncStandardRiskLevel()
      if (result.error) this.$message.error(result.error)
      if (
        !(this.product.kind === 'vip' && this.form.versionType === 3) &&
        this.coinDetail.maxLever !== null &&
        this.form.lever > this.coinDetail.maxLever
      ) {
        this.form.lever = this.coinDetail.maxLever
      }
    },
    standardRiskMinimumRatio() {
      const amount = finiteNumber(this.form.amount)
      const minimum = finiteNumber(this.coinDetail.minSpace)
      const minimumTrue = finiteNumber(this.coinDetail.minSpaceTrue)
      if (amount === null || amount <= 0 || (minimum !== null && amount < minimum)) return null
      if (minimumTrue === null || minimumTrue <= 0) return 1
      return Math.min(100, Math.ceil((minimumTrue / amount) * 100))
    },
    isRiskLevelDisabled(value) {
      if (!this.isStandardHedge || !this.form.coin) return true
      const amount = finiteNumber(this.form.amount)
      const minimum = finiteNumber(this.coinDetail.minSpace)
      const minimumTrue = finiteNumber(this.coinDetail.minSpaceTrue)
      if (amount === null || amount <= 0 || (minimum !== null && amount < minimum)) return true
      if (minimumTrue !== null && amount <= minimumTrue) return true
      const minimumRatio = this.standardRiskMinimumRatio()
      return minimumRatio !== null && minimumRatio > Number(value)
    },
    syncStandardRiskLevel() {
      if (!this.isStandardHedge) return
      const amount = finiteNumber(this.form.amount)
      const minimum = finiteNumber(this.coinDetail.minSpace)
      if (amount === null || amount <= 0 || (minimum !== null && amount < minimum)) return
      const minimumRatio = this.standardRiskMinimumRatio()
      if (minimumRatio === null) return
      this.form.positionProportion = minimumRatio <= 70 ? 70 : minimumRatio <= 80 ? 80 : 100
    },
    selectRiskLevel(value) {
      if (this.isRiskLevelDisabled(value)) return
      this.form.positionProportion = Number(value)
    },
    async handleBaseCoinChange() {
      this.form.quoteCoin = ''
      this.form.baseAmount = ''
      this.form.quoteAmount = ''
      this.pairIndex = null
      this.pairs = []
      this.baseBalance = null
      this.quoteBalance = null
      if (!this.form.baseCoin) return

      const sequence = ++this.requestSequence
      this.pairLoading = true
      const [pairResult, balanceResult] = await Promise.all([
        loadSpotPairs(this.robotType, this.form.baseCoin),
        loadStrategyBalance(this.robotType, this.form.baseCoin)
      ])
      if (sequence !== this.requestSequence) return
      this.pairs = pairResult.rows
      this.baseBalance = balanceResult.balance
      this.pairLoading = false
      if (pairResult.error) this.$message.error(pairResult.error)
      if (balanceResult.error) this.$message.error(balanceResult.error)
    },
    async handlePairChange() {
      const pair = this.selectedPair
      if (!pair) return
      this.form.quoteCoin = this.form.baseCoin === pair.target_coin
        ? pair.cost_coin
        : pair.target_coin
      this.form.baseAmount = ''
      this.form.quoteAmount = ''
      this.quoteBalance = null
      const result = await loadStrategyBalance(this.robotType, this.form.quoteCoin)
      this.quoteBalance = result.balance
      if (result.error) this.$message.error(result.error)
    },
    validateCommonAmount() {
      const amount = finiteNumber(this.form.amount)
      if (amount === null || amount <= 0) {
        this.$message.warning('请输入有效的投入金额')
        return null
      }

      const minimum = this.product.kind === 'astra'
        ? finiteNumber(this.setup.minimum)
        : finiteNumber(this.coinDetail.minSpace)
      if (minimum !== null && amount < minimum) {
        this.$message.warning(`当前策略投入金额不能低于 ${minimum} USDT`)
        return null
      }

      const spaceIssue = strategySpaceAvailabilityIssue(this.productKey, this.setup.capacity, amount)
      if (spaceIssue) {
        this.$message.warning(spaceIssue)
        return null
      }

      if (this.product.kind === 'term') return amount

      const balance = finiteNumber(this.setup.balance)
      if (this.product.kind === 'hedge') {
        if (String(this.form.coin).toUpperCase() === 'FITFI' && amount > 2000) {
          this.$message.warning('FITFI 投入金额不能超过 2000 USDT')
          return null
        }
        const exceedsBalance = Number(this.form.futureType) === 2
          ? balance !== null && amount > balance
          : balance !== null && amount - 5 > balance
        if (exceedsBalance) {
          this.$message.warning('交易所可用资金不足')
          return null
        }
        return amount
      }

      if (
        ['vip', 'astra'].includes(this.product.kind) &&
        balance !== null &&
        amount - 5 > balance
      ) {
        this.$message.warning('交易所可用资金不足')
        return null
      }

      return amount
    },
    validateMaximum(amount) {
      if (!['hedge', 'term'].includes(this.product.kind) || this.form.infinite) return true
      const maximum = finiteNumber(this.form.maxPosition)
      if (maximum === null || maximum <= 0) {
        this.$message.warning('请输入最大持仓金额，或选择“无限”')
        return false
      }
      if (maximum < amount) {
        this.$message.warning('最大持仓金额不能小于本次投入金额')
        return false
      }
      return true
    },
    validateTermBalance(amount) {
      if (this.product.kind !== 'term') return true
      const contractBalance = finiteNumber(this.setup.balance)
      const spotBalance = finiteNumber(this.setup.spotBalance)
      if (Number(this.robotType) === 0) {
        if (contractBalance !== null && amount * 0.15 > contractBalance) {
          this.$message.warning('Binance U 本位合约可用资金不能低于仓位的 30%')
          return false
        }
        if (spotBalance !== null && amount > spotBalance) {
          this.$message.warning('Binance 现货可用资金不能低于仓位的 70%')
          return false
        }
      } else if (contractBalance !== null && amount - 5 > contractBalance) {
        this.$message.warning('交易所可用资金不足')
        return false
      }
      const minimum = finiteNumber(this.coinDetail.minSpace)
      if (minimum !== null && amount < minimum) {
        this.$message.warning(`当前币种投入金额不能低于 ${minimum} USDT`)
        return false
      }
      return true
    },
    validateSpotPair() {
      if (!this.form.baseCoin || !this.form.quoteCoin || !this.selectedPair) {
        this.$message.warning('请选择完整的现货交易对')
        return false
      }
      return true
    },
    validateBb() {
      if (!this.validateSpotPair()) return false
      const baseAmount = finiteNumber(this.form.baseAmount)
      const quoteAmount = finiteNumber(this.form.quoteAmount)

      if (baseAmount !== null && baseAmount > 0) {
        if (this.baseMinimum !== null && baseAmount < this.baseMinimum) {
          this.$message.warning(`${this.form.baseCoin} 仓位不能低于 ${this.baseMinimum}`)
          return false
        }
        if (Number(this.baseBalance) === 0) {
          this.$message.warning(`${this.form.baseCoin} 没有可用余额`)
          return false
        }
        if (this.baseBalance !== null && baseAmount > Number(this.baseBalance) * 1.3) {
          this.$message.warning(`${this.form.baseCoin} 仓位不能超过 ${Number(this.baseBalance) * 1.3}`)
          return false
        }
      }

      if (quoteAmount !== null && quoteAmount > 0) {
        if (this.quoteMinimum !== null && quoteAmount < this.quoteMinimum) {
          this.$message.warning(`${this.form.quoteCoin} 仓位不能低于 ${this.quoteMinimum}`)
          return false
        }
        if (Number(this.quoteBalance) === 0) {
          this.$message.warning(`${this.form.quoteCoin} 没有可用余额`)
          return false
        }
        if (this.quoteBalance !== null && quoteAmount > Number(this.quoteBalance) * 1.3) {
          this.$message.warning(`${this.form.quoteCoin} 仓位不能超过 ${Number(this.quoteBalance) * 1.3}`)
          return false
        }
      }

      return true
    },
    validateGrid() {
      if (!this.validateSpotPair()) return false
      const minPrice = finiteNumber(this.form.minPrice)
      const maxPrice = finiteNumber(this.form.maxPrice)
      const gridNumber = finiteNumber(this.form.gridNumber)
      const baseAmount = finiteNumber(this.form.baseAmount)
      const quoteAmount = finiteNumber(this.form.quoteAmount)
      if (minPrice === null || maxPrice === null || minPrice <= 0 || maxPrice <= minPrice) {
        this.$message.warning('网格最高价必须大于最低价')
        return false
      }
      if (gridNumber === null || gridNumber < 2 || gridNumber > 150 || !Number.isInteger(gridNumber)) {
        this.$message.warning('网格数量必须是 2 到 150 之间的整数')
        return false
      }
      if ((baseAmount === null || baseAmount <= 0) && (quoteAmount === null || quoteAmount <= 0)) {
        this.$message.warning('请至少填写一侧投入金额')
        return false
      }
      if (Number(this.robotType) === 0 && this.gridSingleProfit !== null && this.gridSingleProfit < 0.01) {
        this.$message.warning('Binance 单网格利润率必须大于 1%')
        return false
      }
      if (Number(this.robotType) === 1 && this.gridSingleProfit !== null && this.gridSingleProfit < 0) {
        this.$message.warning('OKX 单网格利润率必须大于 0%')
        return false
      }
      if (baseAmount !== null && this.baseBalance !== null && baseAmount > this.baseBalance) {
        this.$message.warning(`${this.form.baseCoin} 可用余额不足`)
        return false
      }
      if (quoteAmount !== null && this.quoteBalance !== null && quoteAmount > this.quoteBalance) {
        this.$message.warning(`${this.form.quoteCoin} 可用余额不足`)
        return false
      }
      if (baseAmount !== null && this.gridBaseMinimum !== null && baseAmount < this.gridBaseMinimum) {
        this.$message.warning(`${this.form.baseCoin} 投入不能低于 ${this.formatValue(this.gridBaseMinimum)}`)
        return false
      }
      if (quoteAmount !== null && this.gridQuoteMinimum !== null && quoteAmount < this.gridQuoteMinimum) {
        this.$message.warning(`${this.form.quoteCoin} 投入不能低于 ${this.formatValue(this.gridQuoteMinimum)}`)
        return false
      }
      return true
    },
    validateMartin() {
      if (!this.validateSpotPair()) return false
      const firstSpace = finiteNumber(this.form.firstSpace)
      const cycleNumber = finiteNumber(this.form.cycleNumber)
      const firstMultiple = finiteNumber(this.form.firstMultiple)
      const billNumber = finiteNumber(this.form.billNumber)
      const stopRatio = finiteNumber(this.form.stopRatio)
      const stopCallback = finiteNumber(this.form.stopCallback)
      const fullStopRatio = finiteNumber(this.form.fullStopRatio)
      if ([firstSpace, cycleNumber, firstMultiple, billNumber, stopRatio, stopCallback, fullStopRatio].some(value => value === null)) {
        this.$message.warning('请完整填写马丁策略参数')
        return false
      }
      if (Number(this.robotType) === 0 && firstSpace < 15) {
        this.$message.warning('Binance 马丁策略首单额度不能低于 15 USDT')
        return false
      }
      if (Number(this.robotType) === 1 && this.selectedPair) {
        const pairMinimum = finiteNumber(this.selectedPair.min_qty_true)
        const okxMinimum = pairMinimum === null ? null : Math.trunc(pairMinimum * 1.5) + 1
        if (okxMinimum !== null && firstSpace < okxMinimum) {
          this.$message.warning(`OKX 马丁策略首单额度不能低于 ${okxMinimum} USDT`)
          return false
        }
      }
      if (
        firstSpace <= 0 ||
        firstMultiple <= 0 ||
        cycleNumber < 0 ||
        !Number.isInteger(cycleNumber) ||
        billNumber < 2 ||
        billNumber > 32 ||
        !Number.isInteger(billNumber)
      ) {
        this.$message.warning('首单额度、首单倍数和做单数量必须为有效正数')
        return false
      }
      if (stopRatio <= 0 || stopCallback < 0 || fullStopRatio <= 0 || fullStopRatio >= 100) {
        this.$message.warning('止盈、回调和满单止损比例不在有效范围')
        return false
      }
      return true
    },
    submit() {
      if (this.blockingIssue) {
        this.$message.warning(this.blockingIssue)
        return
      }

      const common = {
        productKey: this.productKey,
        robotType: this.robotType,
        robotName: this.form.robotName
      }

      if (this.product.kind === 'bb') {
        if (!this.validateBb()) return
        this.$emit('submit', {
          ...common,
          baseCoin: this.form.baseCoin,
          quoteCoin: this.form.quoteCoin,
          baseAmount: finiteNumber(this.form.baseAmount),
          quoteAmount: finiteNumber(this.form.quoteAmount)
        })
        return
      }

      if (this.product.kind === 'grid') {
        if (!this.validateGrid()) return
        this.$emit('submit', {
          ...common,
          baseCoin: this.form.baseCoin,
          quoteCoin: this.form.quoteCoin,
          minPrice: finiteNumber(this.form.minPrice),
          maxPrice: finiteNumber(this.form.maxPrice),
          gridNumber: finiteNumber(this.form.gridNumber),
          baseAmount: finiteNumber(this.form.baseAmount),
          quoteAmount: finiteNumber(this.form.quoteAmount),
          gridMode: this.form.gridMode
        })
        return
      }

      if (this.product.kind === 'martin') {
        if (!this.validateMartin()) return
        const config = buildMartinConfig(this.form.strategyType, this.form.billNumber)
        const firstSpace = finiteNumber(this.form.firstSpace)
        const fullSpace = config.replenishmentConf.reduce(
          (sum, item) => sum + Number(item.multiple) * firstSpace,
          firstSpace * finiteNumber(this.form.firstMultiple)
        )
        if (fullSpace > 9999999999) {
          this.$message.warning('满单仓位不能超过 9,999,999,999 USDT')
          return
        }
        this.$emit('submit', {
          ...common,
          baseCoin: this.form.baseCoin,
          quoteCoin: this.form.quoteCoin,
          strategyType: this.form.strategyType,
          trendCycle: this.form.trendCycle,
          firstSpace,
          cycleNumber: finiteNumber(this.form.cycleNumber),
          firstMultiple: finiteNumber(this.form.firstMultiple),
          billNumber: finiteNumber(this.form.billNumber),
          stopRatio: finiteNumber(this.form.stopRatio),
          stopCallback: finiteNumber(this.form.stopCallback),
          trackStop: this.form.trackStop,
          replenishmentConf: config.replenishmentConf,
          replenishmentCallback: config.replenishmentCallback,
          trackReplenishment: this.form.trackReplenishment,
          fullStop: this.form.fullStop,
          fullStopRatio: finiteNumber(this.form.fullStopRatio),
          fullSpace,
          trendCycleSwitch: this.form.trendCycleSwitch
        })
        return
      }

      if (this.product.kind !== 'astra' && !this.form.coin) {
        this.$message.warning('请选择交易币种')
        return
      }
      const amount = this.validateCommonAmount()
      if (amount === null || !this.validateMaximum(amount) || !this.validateTermBalance(amount)) return
      if (['hedge', 'vip'].includes(this.product.kind) && (!this.form.lever || Number(this.form.lever) < 1)) {
        this.$message.warning('请输入有效的杠杆倍数')
        return
      }

      const compensation = finiteNumber(this.form.lossTransferRate)
      if (
        this.product.kind === 'hedge' &&
        this.form.stopLoss !== 0 &&
        (compensation === null || compensation < 0 || compensation > 100)
      ) {
        this.$message.warning('补亏损比例应在 0 到 100 之间')
        return
      }

      this.$emit('submit', {
        ...common,
        coin: this.form.coin,
        amount,
        lever: this.form.lever,
        stopLoss: this.form.stopLoss,
        lossTransferRate: compensation,
        positionProportion: this.form.positionProportion,
        versionType: this.form.versionType,
        direction: this.form.direction,
        futureType: this.form.futureType,
        infinite: this.form.infinite,
        maxPosition: finiteNumber(this.form.maxPosition)
      })
    },
    coinOptionLabel(item) {
      return item.minSpace === null
        ? item.coin
        : `${item.coin}（最小 ${this.formatValue(item.minSpace)} USDT）`
    },
    formatValue(value) {
      const number = finiteNumber(value)
      if (number === null) return '--'
      return number.toLocaleString(languageIntlLocale(), { maximumFractionDigits: 8 })
    }
  }
}
</script>

<style>
.strategy-modal {
  position: fixed;
  z-index: 150;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(20,17,38,.46);
  animation: up-fade-in .18s ease;
}

.strategy-modal__panel {
  width: 520px;
  max-width: 92vw;
  max-height: 84vh;
  overflow-y: auto;
  padding: 28px;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 24px 64px rgba(20,16,45,.28);
  animation: strategy-pop .2s ease;
}

@keyframes strategy-pop {
  from {
    opacity: 0;
    transform: translateY(10px) scale(.985);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.strategy-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 18px;
}

.strategy-modal__header h2 {
  margin: 0;
  color: #1b1f2a;
  font-size: 18px;
  font-weight: 800;
}

.strategy-modal__header button {
  display: flex;
  width: 30px;
  height: 30px;
  align-items: center;
  justify-content: center;
  color: #8a90a0;
  border: 0;
  border-radius: 8px;
  background: transparent;
  font-size: 15px;
}

.strategy-modal__header button:hover {
  background: #f5f6f9;
}

.strategy-field {
  margin-bottom: 13px;
}

.strategy-field > label,
.strategy-field__label-row > label:first-child {
  display: block;
  margin-bottom: 7px;
  color: #5a6072;
  font-size: 13.5px;
  font-weight: 600;
}

.strategy-field input:not([type="checkbox"]),
.strategy-field select {
  width: 100%;
  height: 42px;
  outline: 0;
  color: #1b1f2a;
  border: 1px solid #dfe3ec;
  border-radius: 8px;
  padding: 0 13px;
  background: #fafbfd;
  font-size: 14px;
}

.strategy-field select {
  padding-right: 34px;
  background-color: #fff;
  cursor: pointer;
}

.strategy-field input:focus,
.strategy-field select:focus {
  border-color: #7aa7ff;
  box-shadow: 0 0 0 3px rgba(31,107,255,.10);
}

.strategy-field input:disabled,
.strategy-field select:disabled {
  cursor: not-allowed;
  opacity: .6;
}

.strategy-exchange-readonly {
  width: 100%;
  height: 42px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 13px;
  color: #1b1f2a;
  border: 1px solid #dfe3ec;
  border-radius: 8px;
  background: #f7f8fb;
  font-size: 14px;
  box-sizing: border-box;
}

.strategy-exchange-readonly i {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #0e9f5c;
}

.strategy-exchange-readonly strong {
  font-weight: 700;
}

.strategy-filter-select {
  width: 100%;
}

.strategy-filter-select .el-input__inner {
  height: 42px;
  padding-right: 36px;
  color: #1b1f2a;
  border-color: #dfe3ec;
  border-radius: 8px;
  background: #fff;
  font-size: 14px;
  line-height: 42px;
}

.strategy-filter-select .el-input__inner:focus,
.strategy-filter-select .el-input.is-focus .el-input__inner {
  border-color: #7aa7ff;
  box-shadow: 0 0 0 3px rgba(31,107,255,.10);
}

.strategy-filter-select .el-input.is-disabled .el-input__inner {
  color: #8a90a0;
  background: #f5f6f9;
}

.strategy-filter-select .el-input__suffix {
  right: 8px;
}

.strategy-field > small {
  display: block;
  margin-top: 6px;
  color: #8a90a0;
  font-size: 11px;
  line-height: 1.55;
}

.strategy-product-readonly {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 13px;
  border: 1px solid #eef0f5;
  border-radius: 8px;
  background: #f7f8fb;
}

.strategy-product-readonly i {
  width: 8px;
  height: 8px;
  flex: 0 0 auto;
  border-radius: 3px;
}

.strategy-product-readonly strong {
  font-size: 14px;
  font-weight: 800;
}

.strategy-interface-state {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 13px;
  padding: 10px 13px;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.55;
}

.strategy-interface-state.loading {
  color: #1550cc;
  background: #eff5fe;
}

.strategy-interface-state.error {
  color: #b42318;
  background: #fceae8;
}

.strategy-two-column {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.strategy-input-unit {
  position: relative;
}

.strategy-input-unit input {
  padding-right: 72px !important;
}

.strategy-input-unit > span {
  position: absolute;
  top: 50%;
  right: 13px;
  color: #8a90a0;
  font-size: 12px;
  font-weight: 600;
  transform: translateY(-50%);
}

.strategy-quick {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

.strategy-quick button {
  flex: 1;
  color: #1f6bff;
  border: 0;
  border-radius: 7px;
  padding: 7px 0;
  background: #f3f7fe;
  font-size: 12.5px;
  font-weight: 700;
}

.strategy-quick button:hover {
  background: #dce8fd;
}

.strategy-balance {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  padding: 10px 13px;
  border: 1px solid #dce8fd;
  border-radius: 8px;
  background: #f3f7fe;
}

.strategy-balance span {
  color: #5a6072;
  font-size: 12.5px;
  font-weight: 600;
}

.strategy-balance strong {
  color: #1f6bff;
  font-size: 13.5px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.strategy-options {
  display: flex;
  gap: 8px;
}

.strategy-options button {
  flex: 1;
  color: #5a6072;
  border: 1px solid #dfe3ec;
  border-radius: 8px;
  padding: 9px 8px;
  background: #fff;
  font-size: 12.5px;
  font-weight: 700;
}

.strategy-options button.active {
  color: #1f6bff;
  border-color: #a9c4f7;
  background: #f3f7fe;
}

.strategy-loss-ratio__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 7px;
}

.strategy-loss-ratio__header label {
  margin: 0;
}

.strategy-loss-ratio__header strong {
  color: #1f6bff;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.strategy-loss-ratio__slider {
  --loss-progress: 70%;
  width: 100%;
  height: 18px;
  margin: 0;
  padding: 0;
  appearance: none;
  -webkit-appearance: none;
  background: transparent;
  cursor: pointer;
  outline: none;
}

.strategy-loss-ratio__slider::-webkit-slider-runnable-track {
  width: 100%;
  height: 6px;
  border: 0;
  border-radius: 999px;
  background: linear-gradient(to right, #1f6bff 0, #1f6bff var(--loss-progress), #dfe3ec var(--loss-progress), #dfe3ec 100%);
}

.strategy-loss-ratio__slider::-webkit-slider-thumb {
  width: 16px;
  height: 16px;
  margin-top: -5px;
  appearance: none;
  -webkit-appearance: none;
  border: 3px solid #fff;
  border-radius: 50%;
  background: #1f6bff;
  box-shadow: 0 0 0 1px #9ab9f5;
}

.strategy-loss-ratio__slider::-moz-range-track {
  height: 6px;
  border: 0;
  border-radius: 999px;
  background: #dfe3ec;
}

.strategy-loss-ratio__slider::-moz-range-progress {
  height: 6px;
  border-radius: 999px;
  background: #1f6bff;
}

.strategy-loss-ratio__slider::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border: 3px solid #fff;
  border-radius: 50%;
  background: #1f6bff;
  box-shadow: 0 0 0 1px #9ab9f5;
}

.strategy-risk-level__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.strategy-risk-level__header label {
  margin: 0;
  color: #5a6072;
  font-size: 13.5px;
  font-weight: 600;
  cursor: help;
}

.strategy-risk-level__header > span {
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
}

.strategy-risk-level__header > span.is-low {
  color: #078856;
  background: #e8f7f0;
}

.strategy-risk-level__header > span.is-middle {
  color: #b66a00;
  background: #fff3df;
}

.strategy-risk-level__header > span.is-high {
  color: #d93737;
  background: #fdeaea;
}

.strategy-risk-level__steps {
  position: relative;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.strategy-risk-level__track {
  position: absolute;
  top: 4px;
  right: 7px;
  left: 7px;
  height: 6px;
  border-radius: 999px;
  background: #dfe3ec;
}

.strategy-risk-level__track-active {
  position: absolute;
  top: 4px;
  right: 7px;
  left: 7px;
  height: 6px;
  border-radius: 999px;
  background: linear-gradient(90deg, #2cc197 0%, #ffc025 62.5%, #dc4d4d 100%);
  clip-path: inset(0 calc(100% - var(--risk-progress)) 0 0 round 999px);
}

.strategy-risk-level__steps button {
  position: relative;
  z-index: 1;
  display: flex;
  min-width: 0;
  flex: 0 0 14px;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  padding: 0;
  color: #79808f;
  border: 0;
  background: transparent;
  font-size: 11px;
  cursor: pointer;
}

.strategy-risk-level__steps button i {
  width: 14px;
  height: 14px;
  box-sizing: border-box;
  border: 2px solid #fff;
  border-radius: 50%;
  background: #dfe3ec;
  box-shadow: 0 0 0 1px #c9cfdb;
}

.strategy-risk-level__steps button span {
  white-space: nowrap;
}

.strategy-risk-level__steps button.active i {
  background: #1f6bff;
  box-shadow: 0 0 0 2px rgba(31,107,255,.18);
}

.strategy-risk-level__steps button.active span {
  color: #1f6bff;
  font-weight: 700;
}

.strategy-risk-level__steps button.disabled {
  cursor: not-allowed;
  opacity: .38;
}

.strategy-field__label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.strategy-checkbox {
  display: inline-flex !important;
  align-items: center;
  gap: 6px;
  margin: 0 !important;
  color: #5a6072;
  font-size: 12px !important;
}

.strategy-checkbox input {
  accent-color: #1f6bff;
}

.strategy-infinite {
  padding: 11px 13px;
  color: #5a6072;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background: #f8fafc;
  font-size: 12px;
}

.strategy-interface-errors {
  margin-bottom: 10px;
}

.strategy-interface-errors span {
  display: block;
  color: #c2410c;
  font-size: 11px;
  line-height: 1.55;
}

.strategy-risk {
  margin-bottom: 18px;
  padding: 11px 13px;
  color: #0b7a47;
  border-radius: 8px;
  background: #e8f6ee;
  font-size: 12.5px;
  line-height: 1.6;
}

.strategy-modal__footer {
  display: flex;
  gap: 10px;
}

.strategy-modal__footer button {
  flex: 1;
  color: #3a3f4e;
  border: 1px solid #dcdfe8;
  border-radius: 8px;
  padding: 11px 0;
  background: #fff;
  font-size: 14px;
  font-weight: 600;
}

.strategy-modal__footer button.primary {
  color: #fff;
  border: 0;
  background: #1f6bff;
  font-weight: 700;
}

.strategy-modal__footer button:hover:not(:disabled) {
  border-color: #a9c4f7;
}

.strategy-modal__footer button.primary:hover:not(:disabled) {
  background: #1558db;
}

.strategy-modal__footer button:disabled {
  cursor: not-allowed;
  opacity: .55;
}

@media (max-width: 680px) {
  .strategy-modal {
    align-items: flex-start;
    padding: 14px;
  }

  .strategy-modal__panel {
    max-width: 100%;
    max-height: calc(100vh - 28px);
    padding: 22px;
  }

  .strategy-two-column {
    grid-template-columns: 1fr;
  }
}
</style>
